export type VisualMode = 'idle' | 'focus' | 'reduced';
export type QualityTier = 'low' | 'balanced' | 'high';

export interface VisualSettings {
  mode: VisualMode;
  reducedMotion: boolean;
  quality: QualityTier;
  animationSpeed: number;
}

export interface FishParts {
  body: React.ReactNode;
  bodyMarkings: React.ReactNode;
  tailBase: React.ReactNode;
  tailMiddle: React.ReactNode;
  tailFin: React.ReactNode;
  dorsalFin: React.ReactNode;
  pectoralFinL: React.ReactNode;
  pectoralFinR: React.ReactNode;
  pelvicFinL: React.ReactNode;
  pelvicFinR: React.ReactNode;
  analFin: React.ReactNode;
  eye: React.ReactNode;
  eyelid: React.ReactNode;
  cheek: React.ReactNode;
  mouth: React.ReactNode;
  highlightLayer: React.ReactNode;
}

export interface FishAnimationState {
  swimPhase: number;
  tailWavePhase: number;
  finFlutterPhase: number;
  verticalDrift: number;
  blinkProgress: number;
  lookTarget: { x: number; y: number };
  expression: 'neutral' | 'happy' | 'feeding' | 'discovery' | 'curious';
  expressionProgress: number;
}

export interface PlantAnimationState {
  swayPhase: number;
  stemBend: number;
}

export interface DecorAnimationState {
  glowPhase: number;
  particlePhase: number;
}

export interface EnvironmentAnimationState {
  causticPhase: number;
  bubblePhases: number[];
  rayPhase: number;
}

export type ReactionType = 'happy' | 'feeding' | 'discovery';

export const QUALITY_CONFIG: Record<QualityTier, {
  maxBubbles: number;
  maxParticles: number;
  enableSecondaryAnimations: boolean;
  enableParticles: boolean;
  simplifyGradients: boolean;
}> = {
  low: {
    maxBubbles: 6,
    maxParticles: 3,
    enableSecondaryAnimations: false,
    enableParticles: false,
    simplifyGradients: true,
  },
  balanced: {
    maxBubbles: 12,
    maxParticles: 8,
    enableSecondaryAnimations: true,
    enableParticles: true,
    simplifyGradients: false,
  },
  high: {
    maxBubbles: 20,
    maxParticles: 15,
    enableSecondaryAnimations: true,
    enableParticles: true,
    simplifyGradients: false,
  },
};

export const MODE_CONFIG: Record<VisualMode, {
  fishSpeedMultiplier: number;
  bubbleSpeedMultiplier: number;
  plantSwayMultiplier: number;
  expressionFrequencyMultiplier: number;
  enableContinuousSwim: number;
  environmentMotionMultiplier: number;
}> = {
  idle: {
    fishSpeedMultiplier: 1.0,
    bubbleSpeedMultiplier: 1.0,
    plantSwayMultiplier: 1.0,
    expressionFrequencyMultiplier: 1.0,
    enableContinuousSwim: 1,
    environmentMotionMultiplier: 1.0,
  },
  focus: {
    fishSpeedMultiplier: 0.6,
    bubbleSpeedMultiplier: 0.5,
    plantSwayMultiplier: 0.4,
    expressionFrequencyMultiplier: 0.25,
    enableContinuousSwim: 1,
    environmentMotionMultiplier: 0.45,
  },
  reduced: {
    fishSpeedMultiplier: 0,
    bubbleSpeedMultiplier: 0,
    plantSwayMultiplier: 0,
    expressionFrequencyMultiplier: 0,
    enableContinuousSwim: 0,
    environmentMotionMultiplier: 0,
  },
};